import { useMemo } from "react";
import type {
  TradeOrder,
  VybeOrderInternal,
  VybeDealInternal,
} from "../types";
import { dealOpenTimeMs } from "../types";
import type { DateFilter } from "../stores/useTradeHistoryStore";
import { useTradeHistoryStore } from "../stores/useTradeHistoryStore";

// Generic filter configuration
interface FilterConfig {
  symbol: string;
  dateFilter: DateFilter;
  customDateFrom?: string;
  customDateTo?: string;
}

// Date range utilities
const parseCustomDate = (dateStr: string, isEndOfDay: boolean = false) => {
  const currentYear = new Date().getFullYear();
  const [day, month] = dateStr.split(".");
  
  if (isEndOfDay) {
    return new Date(
      currentYear,
      parseInt(month) - 1,
      parseInt(day),
      23,
      59,
      59,
      999
    );
  }
  
  return new Date(
    currentYear,
    parseInt(month) - 1,
    parseInt(day),
    0,
    0,
    0,
    0
  );
};

// Generic date filter function
const filterByDate = <T>(
  items: T[],
  dateFilter: DateFilter,
  getTimestamp: (item: T) => number,
  dateRanges: {
    todayStart: number;
    weekAgo: number;
    monthAgo: number;
    threeMonthsAgo: number;
    sixMonthsAgo: number;
    oneYearAgo: number;
  },
  customDateFrom?: string,
  customDateTo?: string
): T[] => {
  switch (dateFilter) {
    case "all":
      return items;

    case "today":
      return items.filter(item => getTimestamp(item) >= dateRanges.todayStart);
    
    case "lastWeek":
      return items.filter(item => getTimestamp(item) >= dateRanges.weekAgo);
    
    case "lastMonth":
      return items.filter(item => getTimestamp(item) >= dateRanges.monthAgo);
    
    case "last3months":
      return items.filter(item => getTimestamp(item) >= dateRanges.threeMonthsAgo);
    
    case "last6months":
      return items.filter(item => getTimestamp(item) >= dateRanges.sixMonthsAgo);
    
    case "lastYear":
      return items.filter(item => getTimestamp(item) >= dateRanges.oneYearAgo);
    
    case "custom":
      if (!customDateFrom || !customDateTo) {
        console.warn("Custom date filter requires from and to dates");
        return items;
      }
      
      const fromDate = parseCustomDate(customDateFrom);
      const toDate = parseCustomDate(customDateTo, true);
      
      return items.filter(item => {
        const timestamp = getTimestamp(item);
        return timestamp >= fromDate.getTime() && timestamp <= toDate.getTime();
      });
    
    default:
      return items;
  }
};

// Hook for filtering positions
export const useFilterPositions = (config: FilterConfig): TradeOrder[] => {
  const allPositions = useTradeHistoryStore((state) => state.allPositions);
  const bySymbol = useTradeHistoryStore((state) => state.bySymbol);
  const todayStart = useTradeHistoryStore((state) => state.todayStart);
  const weekAgo = useTradeHistoryStore((state) => state.weekAgo);
  const monthAgo = useTradeHistoryStore((state) => state.monthAgo);
  const threeMonthsAgo = useTradeHistoryStore((state) => state.threeMonthsAgo);
  const sixMonthsAgo = useTradeHistoryStore((state) => state.sixMonthsAgo);
  const oneYearAgo = useTradeHistoryStore((state) => state.oneYearAgo);

  return useMemo(() => {
    // Get base positions by symbol
    const basePositions = config.symbol === "allsymbols"
      ? allPositions
      : bySymbol.get(config.symbol) || [];

    // Apply date filter
    const dateRanges = {
      todayStart,
      weekAgo,
      monthAgo,
      threeMonthsAgo,
      sixMonthsAgo,
      oneYearAgo,
    };

    return filterByDate(
      basePositions,
      config.dateFilter,
      (position) => new Date(position.closeTime).getTime(),
      dateRanges,
      config.customDateFrom,
      config.customDateTo
    );
  }, [
    allPositions,
    bySymbol,
    config.symbol,
    config.dateFilter,
    config.customDateFrom,
    config.customDateTo,
    todayStart,
    weekAgo,
    monthAgo,
    threeMonthsAgo,
    sixMonthsAgo,
    oneYearAgo,
  ]);
};

// Hook for filtering orders
export const useFilterOrders = (config: FilterConfig): VybeOrderInternal[] => {
  const allOrders = useTradeHistoryStore((state) => state.allOrders);
  const todayStart = useTradeHistoryStore((state) => state.todayStart);
  const weekAgo = useTradeHistoryStore((state) => state.weekAgo);
  const monthAgo = useTradeHistoryStore((state) => state.monthAgo);
  const threeMonthsAgo = useTradeHistoryStore((state) => state.threeMonthsAgo);
  const sixMonthsAgo = useTradeHistoryStore((state) => state.sixMonthsAgo);
  const oneYearAgo = useTradeHistoryStore((state) => state.oneYearAgo);

  return useMemo(() => {
    // Get base orders by symbol
    const baseOrders = config.symbol === "allsymbols"
      ? allOrders
      : allOrders.filter((order) => order.symbol === config.symbol);

    // Apply date filter
    const dateRanges = {
      todayStart,
      weekAgo,
      monthAgo,
      threeMonthsAgo,
      sixMonthsAgo,
      oneYearAgo,
    };

    return filterByDate(
      baseOrders,
      config.dateFilter,
      (order) => order.openTimeMs,
      dateRanges,
      config.customDateFrom,
      config.customDateTo
    );
  }, [
    allOrders,
    config.symbol,
    config.dateFilter,
    config.customDateFrom,
    config.customDateTo,
    todayStart,
    weekAgo,
    monthAgo,
    threeMonthsAgo,
    sixMonthsAgo,
    oneYearAgo,
  ]);
};

// Hook for filtering deals
export const useFilterDeals = (config: FilterConfig): VybeDealInternal[] => {
  const allDeals = useTradeHistoryStore((state) => state.allDeals);
  const todayStart = useTradeHistoryStore((state) => state.todayStart);
  const weekAgo = useTradeHistoryStore((state) => state.weekAgo);
  const monthAgo = useTradeHistoryStore((state) => state.monthAgo);
  const threeMonthsAgo = useTradeHistoryStore((state) => state.threeMonthsAgo);
  const sixMonthsAgo = useTradeHistoryStore((state) => state.sixMonthsAgo);
  const oneYearAgo = useTradeHistoryStore((state) => state.oneYearAgo);

  return useMemo(() => {
    // Get base deals by symbol
    const baseDeals = config.symbol === "allsymbols"
      ? allDeals
      : allDeals.filter((deal) => deal.symbol === config.symbol);

    // Apply date filter
    const dateRanges = {
      todayStart,
      weekAgo,
      monthAgo,
      threeMonthsAgo,
      sixMonthsAgo,
      oneYearAgo,
    };

    return filterByDate(
      baseDeals,
      config.dateFilter,
      (deal) => dealOpenTimeMs(deal),
      dateRanges,
      config.customDateFrom,
      config.customDateTo
    );
  }, [
    allDeals,
    config.symbol,
    config.dateFilter,
    config.customDateFrom,
    config.customDateTo,
    todayStart,
    weekAgo,
    monthAgo,
    threeMonthsAgo,
    sixMonthsAgo,
    oneYearAgo,
  ]);
};

// Combined hook for filtering all trade types (when needed)
export const useFilterTrades = (config: FilterConfig) => {
  const positions = useFilterPositions(config);
  const orders = useFilterOrders(config);
  const deals = useFilterDeals(config);

  return {
    positions,
    orders,
    deals,
  };
};

// Export individual filter functions for use in other hooks
export { filterByDate, parseCustomDate };
